export const PROCESS_STEPS = [
  {
    n: "01",
    title: "Walkthrough & Honest Assessment",
    desc: "We come look at the actual job, listen to what you want, and tell you straight what it needs — no upselling, no pressure.",
  },
  {
    n: "02",
    title: "Detailed Upfront Quote",
    desc: "You get a clear, itemized price before anything starts, so you know exactly what you're paying for.",
  },
  {
    n: "03",
    title: "On-Schedule Build",
    desc: "We keep the work moving. If something unexpected turns up, you hear about it and approve it before we touch it.",
  },
  {
    n: "04",
    title: "Final Walkthrough, No Surprises",
    desc: "We walk the finished job with you and don't call it done until you're genuinely happy with it.",
  },
];

const SERVICES = {
  roofing: {
    slug: "roofing",
    category: "residential",
    path: "/residential/roofing",
    navLabel: "Roofing",
    eyebrow: "RESIDENTIAL",
    cardTitle: "Roofing",
    cardDesc: "Roof replacement and repair that stops the leaks and lasts for decades — with a clear price first.",
    h1: "Roofing Contractor in Monmouth County, NJ",
    metaTitle: "Roofing Contractor Monmouth County NJ | AIM High",
    metaDescription:
      "Roof replacement & repair by a licensed NJ contractor with 20+ years experience. Serving Monmouth, Union, Middlesex & Ocean County. Free roof estimates.",
    keyword: "roofing contractor Monmouth County NJ",
    heroSubline:
      "A roof you won't have to think about for decades — with a clear quote before we start.",
    outcomeHeadline: "A roof you'll stop worrying about",
    soundFamiliar: [
      "Water stains showing up on your ceiling after every storm",
      "Missing or curling shingles you can see from the driveway",
      "Not sure if you need a repair or a full replacement",
      "Worried a new roof will blow your budget with surprise costs",
    ],
    intro: [
      "If your roof is leaking, losing shingles, or just old enough that every heavy rain makes you nervous, you're not imagining the problem — and putting it off usually makes it more expensive. A small leak turns into soaked insulation, then stained drywall, then mold. The hard part is knowing whether a patch will hold or whether it's time for a new roof, and how much either one should really cost.",
      "That's where we come in. We climb up, look at the actual condition of your roof — decking, flashing, valleys, vents — and tell you honestly whether a targeted repair makes sense or a full replacement will save you money over the next few years. Then you get a detailed, upfront quote. No vague ranges, no pressure, and no surprise line items showing up at the end.",
      "When it's time to build, we protect your landscaping, keep the site clean, and finish on the schedule we promised. If we open things up and find rotted decking or hidden damage, you'll hear about it and approve the fix before we go further — you're never handed a shock at the end of the job. You're covered the whole way: we're licensed, insured, and NJ HIC registered, with 20+ years and a former union carpenter running the crew. And the estimate is always free.",
    ],
    beforeAfter: [
      { caption: "From storm-damaged and leaking to a clean, watertight roof — Holmdel, NJ" },
      { caption: "Aging shingles replaced with a durable new roof — Colts Neck, NJ" },
      { caption: "Worn flashing and valleys rebuilt right — Marlboro, NJ" },
    ],
    gallery: [
      { alt: "New roof replacement in Holmdel NJ", filename: "roofing-contractor-holmdel-nj-1.jpg" },
      { alt: "Shingle roofing project in Colts Neck NJ", filename: "roofing-contractor-colts-neck-nj-2.jpg" },
      { alt: "Roof repair in Marlboro NJ", filename: "roofing-contractor-marlboro-nj-3.jpg" },
      { alt: "Roof flashing detail in Freehold NJ", filename: "roofing-contractor-freehold-nj-4.jpg" },
      { alt: "Residential roofing in Middletown NJ", filename: "roofing-contractor-middletown-nj-5.jpg" },
      { alt: "Roof replacement in Monmouth County NJ", filename: "roofing-contractor-monmouth-county-nj-6.jpg" },
    ],
    faqs: [
      {
        q: "How long will my roof replacement take?",
        a: "Most homes are done in one to three days depending on size, pitch, and weather. We'll give you a firm timeline in your quote and keep you posted each day.",
      },
      {
        q: "Do I really need a full replacement, or can you repair it?",
        a: "Sometimes a repair is genuinely enough — and we'll tell you when it is. If a replacement will actually save you money over the next few years, we'll show you why so you can decide.",
      },
      {
        q: "What happens if you find hidden damage under the shingles?",
        a: "We stop and show you. You'll get the cost to fix rotted decking or bad flashing and approve it before we continue — no surprise charges at the end.",
      },
      {
        q: "Are you licensed and insured for roofing work in NJ?",
        a: "Yes. We're fully licensed, insured, and NJ HIC registered, so your home and our crew are covered from start to finish.",
      },
    ],
    siblings: ["siding", "gutters", "basement-finishing"],
  },

  siding: {
    slug: "siding",
    category: "residential",
    path: "/residential/siding",
    navLabel: "Siding",
    eyebrow: "RESIDENTIAL",
    cardTitle: "Siding",
    cardDesc: "New siding that seals out the weather and gives your home a fresh, finished look.",
    h1: "Siding Installation & Replacement in Monmouth County, NJ",
    metaTitle: "Siding Contractor Monmouth County NJ | AIM High",
    metaDescription:
      "Professional siding installation, replacement & repair in Central NJ. Honest pricing, no surprises, jobs kept on schedule. Licensed & insured. Free quote.",
    keyword: "siding contractor Monmouth County NJ",
    heroSubline:
      "Siding that seals out the weather and makes your whole house look new — priced clearly, before we start.",
    outcomeHeadline: "A home that looks new and stays dry",
    soundFamiliar: [
      "Cracked, faded, or warped siding that drags down your curb appeal",
      "Drafty rooms and heating bills that keep creeping up",
      "Soft spots or rot you're worried about behind the panels",
      "No idea what fresh siding should actually cost",
    ],
    intro: [
      "Old siding does more than look tired. Cracked panels and failing seams let water and cold air get behind your walls, which shows up as drafty rooms, higher energy bills, and — if it's been going on a while — rot you can't see. When it's this bad, a coat of paint won't fix it, and guessing your way through a siding project is an easy way to overspend.",
      "We start by looking at what's really happening behind the panels, so you know whether you need a full re-side or just targeted repairs. Then you get an honest, itemized quote and a straight recommendation on materials that fit your home and your budget — not the most expensive option on the shelf. You'll know the price before a single panel comes off.",
      "During the work we keep your property tidy and stick to the schedule. If we pull panels and find water damage or rot underneath, we'll show you, explain it, and get your OK before we fix it — so there are no surprises waiting at the end. You're in good hands the whole time: licensed, insured, NJ HIC registered, 20+ years of work behind us, and a free estimate to get started.",
    ],
    beforeAfter: [
      { caption: "Faded, cracked panels replaced with crisp new siding — Freehold, NJ" },
      { caption: "A dated exterior brought back to life — Manalapan, NJ" },
      { caption: "Weather-worn walls resealed and refreshed — Howell, NJ" },
    ],
    gallery: [
      { alt: "New siding installation in Freehold NJ", filename: "siding-contractor-freehold-nj-1.jpg" },
      { alt: "Siding replacement in Manalapan NJ", filename: "siding-contractor-manalapan-nj-2.jpg" },
      { alt: "Vinyl siding project in Howell NJ", filename: "siding-contractor-howell-nj-3.jpg" },
      { alt: "Exterior siding in Colts Neck NJ", filename: "siding-contractor-colts-neck-nj-4.jpg" },
      { alt: "Home siding in Middletown NJ", filename: "siding-contractor-middletown-nj-5.jpg" },
      { alt: "Siding installation in Monmouth County NJ", filename: "siding-contractor-monmouth-county-nj-6.jpg" },
    ],
    faqs: [
      {
        q: "How do I know if I need new siding or just repairs?",
        a: "We check for warping, cracks, soft spots, and moisture behind the panels. If a repair will hold, we'll say so. If a full re-side is the smarter spend, we'll show you why.",
      },
      {
        q: "Will new siding actually lower my energy bills?",
        a: "In most older homes, yes. Sealing the gaps and adding proper wrap keeps drafts out and makes your heating and cooling work less — you'll usually feel the difference in comfort right away.",
      },
      {
        q: "What if there's rot behind the old siding?",
        a: "We'll show you exactly what we found, explain the fix, and get your approval before doing the work. You won't get a surprise bill at the end.",
      },
      {
        q: "How long does a siding job take?",
        a: "Most homes take several days to about a week depending on size and detail. You'll get a firm timeline in your quote and updates as we go.",
      },
    ],
    siblings: ["roofing", "gutters", "kitchen-remodeling"],
  },

  gutters: {
    slug: "gutters",
    category: "residential",
    path: "/residential/gutters",
    navLabel: "Gutters",
    eyebrow: "RESIDENTIAL",
    cardTitle: "Gutters",
    cardDesc: "Gutters that actually move water away from your home — installed, replaced, or repaired.",
    h1: "Gutter Installation & Replacement in Monmouth County, NJ",
    metaTitle: "Gutter Installation Monmouth County NJ | AIM High",
    metaDescription:
      "Gutter installation, replacement & repair across Monmouth, Union & Middlesex County NJ. Protect your home with a licensed local contractor. Free estimate.",
    keyword: "gutter installation Monmouth County NJ",
    heroSubline:
      "Gutters that carry water away from your home instead of into your basement — done right the first time.",
    outcomeHeadline: "Water that goes where it should",
    soundFamiliar: [
      "Water spilling over the edge every time it rains hard",
      "Pooling around your foundation or a damp basement",
      "Sagging, leaking, or pulling-away gutters",
      "Tired of climbing a ladder to clean out clogs",
    ],
    intro: [
      "Gutters are easy to ignore until they cause a problem — and by then it's usually an expensive one. When they clog, sag, or aren't sized right, rainwater spills over and runs straight down against your foundation. That's how you end up with a wet basement, cracked concrete, eroded landscaping, and rotting fascia. If you're seeing overflow or pooling, your gutters aren't doing their one job.",
      "We look at how water actually moves off your roof and around your house, then set you up with properly sized gutters and downspouts that carry it well away from the foundation. You'll get a clear quote first, an honest take on whether you need full replacement or a repair, and options like guards if you're tired of cleaning them out every fall.",
      "The install is quick, clean, and on schedule, and we'll point out anything we notice up there — like worn fascia or roof edge issues — before it becomes a bigger bill. No surprises, no upsell. You're covered start to finish: licensed, insured, NJ HIC registered, with 20+ years of experience and a free estimate whenever you're ready.",
    ],
    beforeAfter: [
      { caption: "Overflowing, sagging gutters swapped for a clean-running system — Wall, NJ" },
      { caption: "New downspouts routed water away from the foundation — Middletown, NJ" },
    ],
    gallery: [
      { alt: "Gutter installation in Wall NJ", filename: "gutter-installation-wall-nj-1.jpg" },
      { alt: "Gutter replacement in Middletown NJ", filename: "gutter-installation-middletown-nj-2.jpg" },
      { alt: "Seamless gutters in Colts Neck NJ", filename: "gutter-installation-colts-neck-nj-3.jpg" },
      { alt: "Downspout install in Holmdel NJ", filename: "gutter-installation-holmdel-nj-4.jpg" },
      { alt: "Gutter guards in Freehold NJ", filename: "gutter-installation-freehold-nj-5.jpg" },
      { alt: "Gutter work in Monmouth County NJ", filename: "gutter-installation-monmouth-county-nj-6.jpg" },
    ],
    faqs: [
      {
        q: "How do I know if my gutters need replacing?",
        a: "If they overflow, sag, leak at the seams, or pull away from the house, they're past repair. We'll take a look and tell you honestly whether a fix or a full replacement makes more sense.",
      },
      {
        q: "Do gutter guards really cut down on cleaning?",
        a: "For most homes, yes — especially under trees. They won't make gutters completely maintenance-free, but they dramatically reduce clogs and ladder trips. We'll tell you if they're worth it for your roof.",
      },
      {
        q: "Can new gutters help my wet basement?",
        a: "Very often. A lot of basement water starts with roof runoff pooling at the foundation. Properly sized gutters and downspouts that carry water away are one of the cheapest fixes there is.",
      },
      {
        q: "How long does a gutter job take?",
        a: "Most homes are done in a day. You'll have a firm timeline in your quote before we start.",
      },
    ],
    siblings: ["roofing", "siding", "basement-finishing"],
  },

  "kitchen-remodeling": {
    slug: "kitchen-remodeling",
    category: "residential",
    path: "/residential/kitchen-remodeling",
    navLabel: "Kitchen Renovations",
    eyebrow: "RESIDENTIAL",
    cardTitle: "Kitchen Renovations",
    cardDesc: "A kitchen you'll actually want to cook in — planned around how you really live.",
    h1: "Kitchen Renovations in Monmouth County, NJ",
    metaTitle: "Kitchen Renovations Monmouth County NJ | AIM High",
    metaDescription:
      "Full kitchen renovations by a former union carpenter with 20+ years experience. Upfront pricing, on-schedule work, no surprise change orders. Free estimate.",
    keyword: "kitchen renovation Monmouth County NJ",
    heroSubline:
      "A kitchen you'll actually want to cook in — built on time, priced upfront, with no surprise change orders.",
    outcomeHeadline: "A kitchen you'll actually want to cook in",
    soundFamiliar: [
      "Not enough counter space or storage for how you really cook",
      "Dated cabinets and layout that make the room feel small",
      "Worried a renovation will spiral way over budget",
      "Dreading weeks of chaos with no working kitchen",
    ],
    intro: [
      "The kitchen is where everyone ends up — so when it's cramped, dated, or just doesn't work the way you cook, you feel it every single day. Maybe there's nowhere to prep, the storage is a mess, or the layout forces you to squeeze past each other. The two things that stop most people from fixing it are the same: fear that the cost will balloon, and fear of living without a kitchen for months.",
      "We plan your renovation around how you actually live — where you cook, where the kids do homework, where everyone gathers — not just what looks good in a showroom. You'll get a detailed, upfront quote covering cabinets, counters, layout, lighting, and finishes, so you know the real number before we start. And because a former union carpenter is running the job, the cabinets sit right, the counters are level, and the details hold up.",
      "We also plan the schedule so your life keeps moving, and we keep the site clean at the end of each day. If we open a wall and find old wiring or a plumbing issue, you'll hear about it and approve the fix before we go further — no surprise change orders at the end. You're covered throughout: licensed, insured, NJ HIC registered, 20+ years in, and the estimate is free.",
    ],
    beforeAfter: [
      { caption: "From a cramped, closed-off galley to an open kitchen made for gathering — Rumson, NJ" },
      { caption: "Dated cabinets and counters replaced with a bright, functional layout — Holmdel, NJ" },
      { caption: "A tired kitchen turned into the heart of the home — Colts Neck, NJ" },
    ],
    gallery: [
      { alt: "Kitchen renovation in Rumson NJ", filename: "kitchen-remodeling-rumson-nj-1.jpg" },
      { alt: "Kitchen renovation in Holmdel NJ", filename: "kitchen-remodeling-holmdel-nj-2.jpg" },
      { alt: "Custom cabinets in Colts Neck NJ", filename: "kitchen-remodeling-colts-neck-nj-3.jpg" },
      { alt: "Kitchen island in Marlboro NJ", filename: "kitchen-remodeling-marlboro-nj-4.jpg" },
      { alt: "Kitchen countertops in Red Bank NJ", filename: "kitchen-remodeling-red-bank-nj-5.jpg" },
      { alt: "Kitchen renovation in Monmouth County NJ", filename: "kitchen-remodeling-monmouth-county-nj-6.jpg" },
    ],
    faqs: [
      {
        q: "Will I be without a kitchen the whole time?",
        a: "Not the whole time, and never longer than we tell you. We plan the sequence to keep the disruption as short as possible and set up a temporary spot so you can still get by day to day.",
      },
      {
        q: "How do you keep the cost from ballooning?",
        a: "You get a detailed, itemized quote before we start, and we design to your budget. If something unexpected turns up behind a wall, we explain it and get your approval before spending a dollar more.",
      },
      {
        q: "Can you help with the design and layout?",
        a: "Absolutely. We'll walk through how you actually use the space and lay it out around that — where you prep, cook, store, and gather — so the finished kitchen works for real life.",
      },
      {
        q: "How long does a kitchen renovation take?",
        a: "Most run a few weeks depending on scope and material lead times. You'll get a realistic schedule in your quote and clear updates the whole way.",
      },
    ],
    siblings: ["bathroom-remodeling", "basement-finishing", "siding"],
  },

  "bathroom-remodeling": {
    slug: "bathroom-remodeling",
    category: "residential",
    path: "/residential/bathroom-remodeling",
    navLabel: "Bathroom Renovations",
    eyebrow: "RESIDENTIAL",
    cardTitle: "Bathroom Renovations",
    cardDesc: "A bathroom that finally works — and looks good enough to show off.",
    h1: "Bathroom Renovations in Monmouth County, NJ",
    metaTitle: "Bathroom Renovations Monmouth County NJ | AIM High",
    metaDescription:
      "Bathroom renovations done right — honest pricing, quality craftsmanship, on schedule. Serving Monmouth, Union, Middlesex & Ocean County. Free estimates.",
    keyword: "bathroom renovation Monmouth County NJ",
    heroSubline:
      "A bathroom that finally works the way you need it to — quality work, honest pricing, done on schedule.",
    outcomeHeadline: "A bathroom you're glad to show off",
    soundFamiliar: [
      "Outdated bathroom you avoid showing guests",
      "Not sure what a realistic budget looks like",
      "Worried a renovation means weeks without a working bathroom",
      "Nervous about hidden water damage behind the tile",
    ],
    intro: [
      "A dated or cramped bathroom is the kind of thing you stop noticing until guests come over — then suddenly you're apologizing for it. Beyond the looks, older bathrooms often hide real problems: a slow leak behind the tile, poor ventilation growing mold, a layout that wastes what little space you have. And most people hesitate to fix it because they can't picture the budget or the idea of losing their only bathroom for weeks.",
      "We take the guessing out of it. You'll get a clear, itemized quote covering everything — tile, vanity, tub or shower, plumbing, lighting, and ventilation — so you know the real cost before we start. We'll also give you a straight read on what's worth spending on and what isn't, so the money goes where it actually matters to you.",
      "Because water and tile hide problems, we check for damage as we open things up. If we find a leak or rot behind the wall, you'll hear about it and approve the fix before we continue — so nothing gets sprung on you at the end. We keep the job on schedule and the site clean, and the craftsmanship holds up because a former union carpenter is running it. Licensed, insured, NJ HIC registered, 20+ years — and the estimate is free.",
    ],
    beforeAfter: [
      { caption: "From a dated, cramped bath to a clean, modern space — Marlboro, NJ" },
      { caption: "A tired tub surround replaced with a walk-in tile shower — Freehold, NJ" },
      { caption: "Small bathroom reworked to feel twice the size — Middletown, NJ" },
    ],
    gallery: [
      { alt: "Bathroom renovation in Marlboro NJ", filename: "bathroom-remodeling-marlboro-nj-1.jpg" },
      { alt: "Tile shower in Freehold NJ", filename: "bathroom-remodeling-freehold-nj-2.jpg" },
      { alt: "Bathroom renovation in Middletown NJ", filename: "bathroom-remodeling-middletown-nj-3.jpg" },
      { alt: "Vanity install in Holmdel NJ", filename: "bathroom-remodeling-holmdel-nj-4.jpg" },
      { alt: "Master bath in Colts Neck NJ", filename: "bathroom-remodeling-colts-neck-nj-5.jpg" },
      { alt: "Bathroom renovation in Monmouth County NJ", filename: "bathroom-remodeling-monmouth-county-nj-6.jpg" },
    ],
    faqs: [
      {
        q: "Will I be without a bathroom the whole renovation?",
        a: "We plan around it. If it's your only bathroom, we sequence the work to get it usable as fast as possible and tell you exactly how many days to plan for.",
      },
      {
        q: "What's a realistic budget for a bathroom renovation?",
        a: "It depends on size and finishes, but you'll never guess — we give you a detailed quote upfront and help you spend where it counts so there are no surprises.",
      },
      {
        q: "What if you find water damage or mold behind the tile?",
        a: "We'll show you what we found, explain the fix, and get your OK before doing the work. You won't be handed a surprise at the end of the job.",
      },
      {
        q: "How long does a bathroom renovation take?",
        a: "Most take one to three weeks depending on scope. You'll get a firm schedule in your quote and updates as we go.",
      },
    ],
    siblings: ["kitchen-remodeling", "basement-finishing", "roofing"],
  },

  "basement-finishing": {
    slug: "basement-finishing",
    category: "residential",
    path: "/residential/basement-finishing",
    navLabel: "Basement Finishing",
    eyebrow: "RESIDENTIAL",
    cardTitle: "Basement Finishing",
    cardDesc: "Turn that unused basement into real living space your family will actually use.",
    h1: "Basement Finishing in Monmouth County, NJ",
    metaTitle: "Basement Finishing Monmouth County NJ | AIM High",
    metaDescription:
      "Turn your basement into real living space. Licensed & insured NJ contractor, 20+ years experience, upfront pricing with no surprises. Free estimate.",
    keyword: "basement finishing Monmouth County NJ",
    heroSubline:
      "Turn your basement into real living space — a family room, gym, or guest suite — with a clear plan and price.",
    outcomeHeadline: "A whole new floor of your home",
    soundFamiliar: [
      "A big empty basement doing nothing but holding boxes",
      "Wishing you had a family room, gym, or guest space",
      "Not sure if moisture down there is a dealbreaker",
      "Worried the project will cost more than it's worth",
    ],
    intro: [
      "You've basically got a whole extra floor sitting down there — and right now it's storing boxes and holding potential. Whether you're dreaming of a movie room, a home gym, a playroom, a home office, or a guest suite, a finished basement is one of the best-value ways to add real living space without adding on to your house. The two things that hold people back are moisture worries and not knowing what it'll cost.",
      "We start by checking the things that actually matter down there — moisture, insulation, ceiling height, and egress — so you know up front whether it's ready to finish or needs a little prep first. Then you get a clear, itemized quote for the whole build: framing, insulation, electrical, flooring, lighting, and the finishes you want. No vague numbers, and no surprise line items at the end.",
      "From there we build it clean and on schedule, treating your home like people still live in it — because they do. If we run into anything unexpected behind the walls, you'll hear about it and approve the fix before we move on. You're covered the whole way: licensed, insured, NJ HIC registered, 20+ years of experience, and a former union carpenter making sure it's done right. The estimate is free whenever you're ready.",
    ],
    beforeAfter: [
      { caption: "From a damp storage room to a family movie night spot — Marlboro, NJ" },
      { caption: "Empty concrete turned into a warm family room — Manalapan, NJ" },
      { caption: "Unused basement finished into a home gym and office — Colts Neck, NJ" },
    ],
    gallery: [
      { alt: "Basement finishing in Marlboro NJ", filename: "basement-finishing-marlboro-nj-1.jpg" },
      { alt: "Finished basement in Manalapan NJ", filename: "basement-finishing-manalapan-nj-2.jpg" },
      { alt: "Basement family room in Colts Neck NJ", filename: "basement-finishing-colts-neck-nj-3.jpg" },
      { alt: "Basement home gym in Holmdel NJ", filename: "basement-finishing-holmdel-nj-4.jpg" },
      { alt: "Basement finishing in Freehold NJ", filename: "basement-finishing-freehold-nj-5.jpg" },
      { alt: "Basement finishing in Monmouth County NJ", filename: "basement-finishing-monmouth-county-nj-6.jpg" },
    ],
    faqs: [
      {
        q: "My basement gets a little damp — can it still be finished?",
        a: "Often yes, once we handle the moisture source first. We check it before anything else and tell you honestly whether it's ready or needs waterproofing prep — we won't finish over a problem.",
      },
      {
        q: "Do I need permits to finish my basement?",
        a: "Usually, yes — especially for electrical and egress. We handle the permitting so it's done to code and won't cause you headaches later.",
      },
      {
        q: "How much value does a finished basement add?",
        a: "It's one of the best returns in home improvement because you're adding usable square footage without building an addition. We'll help you plan it so the money goes toward space you'll actually use.",
      },
      {
        q: "How long does it take to finish a basement?",
        a: "Most projects run a few weeks depending on size and scope. You'll get a realistic timeline in your quote and updates throughout.",
      },
    ],
    siblings: ["kitchen-remodeling", "bathroom-remodeling", "gutters"],
  },

  "general-maintenance": {
    slug: "general-maintenance",
    category: "commercial",
    path: "/commercial/general-maintenance",
    navLabel: "General Maintenance",
    eyebrow: "COMMERCIAL",
    cardTitle: "General Maintenance",
    cardDesc: "One reliable crew to keep your facility running — repairs, upkeep, and fast fixes.",
    h1: "Commercial General Maintenance in NJ & NYC",
    metaTitle: "Commercial Maintenance Contractor NJ | AIM High",
    metaDescription:
      "Commercial general maintenance for hospitals, gyms & facilities across Central NJ. Registered for NYC property maintenance. Reliable, licensed & insured.",
    keyword: "commercial maintenance contractor NJ",
    heroSubline:
      "One reliable crew to keep your facility running — repairs, upkeep, and fixes handled without the runaround.",
    outcomeHeadline: "A facility that just keeps running",
    soundFamiliar: [
      "Juggling a different contractor for every small repair",
      "Waiting days for someone to show up for an urgent fix",
      "Deferred maintenance that keeps turning into bigger problems",
      "No single crew that knows your building",
    ],
    intro: [
      "When you run a facility, the little things add up fast — a failing door, a plumbing leak, drywall damage, worn flooring, a punch list that never gets shorter. Chasing a different specialist for each one wastes your time, and every repair you put off tends to grow into a bigger, more expensive problem. What you really want is one crew that shows up, knows your building, and just handles it.",
      "That's what we do. From hospitals and gyms to offices, restaurants, and retail, we cover the ongoing repairs and upkeep that keep your space safe, clean, and open for business. You get straightforward pricing, honest recommendations on what needs attention now versus what can wait, and work scheduled around your operating hours so we're not in your customers' way.",
      "You'll always know what's happening and what it costs before we act — if a repair turns out to be bigger than it looked, we explain it and get your sign-off first. Reliable, licensed, and insured, with 20+ years of hands-on construction experience behind every visit. And because we're registered with New York City for property maintenance, we can support facilities across NJ and into NYC.",
    ],
    nyc: true,
    clientTypes: ["Hospitals", "Gyms", "Restaurants", "Retail"],
    beforeAfter: [
      { caption: "A worn, high-traffic entryway restored for daily use — Central NJ facility" },
      { caption: "Deferred repairs cleared and the space back in service — NJ commercial building" },
    ],
    gallery: [
      { alt: "Commercial maintenance for a facility in NJ", filename: "commercial-maintenance-contractor-nj-1.jpg" },
      { alt: "Facility repair work in New Jersey", filename: "commercial-maintenance-contractor-nj-2.jpg" },
      { alt: "Commercial building upkeep in NJ", filename: "commercial-maintenance-contractor-nj-3.jpg" },
      { alt: "Property maintenance in NYC", filename: "property-maintenance-nyc-4.jpg" },
      { alt: "Gym facility maintenance in NJ", filename: "commercial-maintenance-contractor-nj-5.jpg" },
      { alt: "Hospital facility repair in NJ", filename: "commercial-maintenance-contractor-nj-6.jpg" },
    ],
    faqs: [
      {
        q: "Can you work around our operating hours?",
        a: "Yes. We schedule around when you're open so we stay out of your customers' and staff's way — including early mornings, evenings, or off-peak days when needed.",
      },
      {
        q: "Do you handle both one-off repairs and ongoing maintenance?",
        a: "Both. We can knock out a single urgent fix or become the crew you call for regular upkeep across your facility.",
      },
      {
        q: "Are you registered to work in New York City?",
        a: "Yes — we're registered with New York City for property maintenance, so we can support facilities in NYC as well as across Central New Jersey.",
      },
      {
        q: "How do you handle pricing on maintenance work?",
        a: "You get clear pricing upfront. If a repair turns out to be bigger than it first looked, we explain it and get your approval before doing the extra work.",
      },
    ],
    siblings: ["restaurant-build-outs", "retail-storefronts", "roofing"],
  },

  "restaurant-build-outs": {
    slug: "restaurant-build-outs",
    category: "commercial",
    path: "/commercial/restaurant-build-outs",
    navLabel: "Restaurant Build-Outs",
    eyebrow: "COMMERCIAL",
    cardTitle: "Restaurant Build-Outs",
    cardDesc: "Build-outs and renovations that get your restaurant open on time and up to code.",
    h1: "Restaurant Build-Out Contractor in New Jersey",
    metaTitle: "Restaurant Build-Out Contractor NJ | AIM High",
    metaDescription:
      "Restaurant build-outs & renovations by an experienced NJ commercial contractor. On-schedule delivery, transparent pricing, union carpenter craftsmanship.",
    keyword: "restaurant build out contractor NJ",
    heroSubline:
      "A restaurant build-out delivered on schedule and up to code — so you can open on time and start serving.",
    outcomeHeadline: "Open on time, built to code",
    soundFamiliar: [
      "Every day before opening is a day you're paying rent with no revenue",
      "Juggling kitchen, dining, code, and inspections at once",
      "Burned before by a contractor who blew the timeline",
      "Need it done right the first time to pass inspection",
    ],
    intro: [
      "In the restaurant business, time is money in the most literal way — every week your space sits half-finished is a week of rent with no covers, no tickets, and no revenue. A build-out has a lot of moving parts too: kitchen infrastructure, dining layout, restrooms, ventilation, finishes, and a stack of code and inspection requirements that all have to line up. One slow or sloppy contractor can push your opening back a month.",
      "We build restaurants like people who understand what a delayed opening costs you. You get a realistic schedule and a clear, itemized quote up front, then a crew that keeps the job moving and communicates instead of going quiet. From the back-of-house build to the dining room your guests will see, the work is done to code and done to last — because a former union carpenter is running it.",
      "You'll always know where the project stands. If an inspection or an existing-condition issue turns up, we flag it immediately, explain your options, and keep things moving rather than leaving you in the dark. Transparent pricing, no surprise change orders at the end, and licensed, insured, experienced work from start to opening night.",
    ],
    beforeAfter: [
      { caption: "An empty shell built out into a service-ready dining room — New Jersey" },
      { caption: "A dated space renovated and reopened on schedule — NJ restaurant" },
    ],
    gallery: [
      { alt: "Restaurant build-out in New Jersey", filename: "restaurant-build-out-contractor-nj-1.jpg" },
      { alt: "Restaurant kitchen construction in NJ", filename: "restaurant-build-out-contractor-nj-2.jpg" },
      { alt: "Restaurant dining room build-out in NJ", filename: "restaurant-build-out-contractor-nj-3.jpg" },
      { alt: "Restaurant renovation in New Jersey", filename: "restaurant-build-out-contractor-nj-4.jpg" },
      { alt: "Commercial kitchen build in NJ", filename: "restaurant-build-out-contractor-nj-5.jpg" },
      { alt: "Restaurant construction in NJ", filename: "restaurant-build-out-contractor-nj-6.jpg" },
    ],
    faqs: [
      {
        q: "How do you keep a build-out on schedule?",
        a: "We plan the sequence realistically up front, keep the crew moving, and communicate constantly. If something threatens the timeline, you hear about it right away — not after the deadline's already blown.",
      },
      {
        q: "Do you handle code and inspection requirements?",
        a: "Yes. We build to code from the start and coordinate the work so inspections go smoothly, which is exactly what keeps your opening date on track.",
      },
      {
        q: "Can you work with my kitchen and equipment layout?",
        a: "Absolutely. We build around your menu, your equipment, and how your team needs to move, so the back-of-house actually works on a busy night.",
      },
      {
        q: "How is pricing handled on a restaurant project?",
        a: "You get a clear, itemized quote before we start. If an existing-condition issue turns up, we explain it and get your approval before doing the extra work — no surprise change orders at the end.",
      },
    ],
    siblings: ["retail-storefronts", "general-maintenance", "kitchen-remodeling"],
  },

  "retail-storefronts": {
    slug: "retail-storefronts",
    category: "commercial",
    path: "/commercial/retail-storefronts",
    navLabel: "Retail & Storefronts",
    eyebrow: "COMMERCIAL",
    cardTitle: "Retail & Storefronts",
    cardDesc: "Storefront and retail build-outs delivered on time and on budget — including mall spaces.",
    h1: "Retail & Storefront Construction in New Jersey",
    metaTitle: "Retail & Storefront Construction NJ | AIM High",
    metaDescription:
      "Storefront and retail build-outs across NJ, including mall storefront projects. Licensed commercial contractor delivering on time and on budget.",
    keyword: "retail construction contractor NJ",
    heroSubline:
      "Retail and storefront build-outs delivered on time and on budget — so you can open the doors and start selling.",
    outcomeHeadline: "Open the doors and start selling",
    soundFamiliar: [
      "A lease clock ticking on an unfinished space",
      "Mall or landlord build-out rules you have to hit exactly",
      "Need a storefront that pulls people in off the sidewalk",
      "Can't afford a contractor who misses the opening date",
    ],
    intro: [
      "For a retail space, your build-out is the difference between paying rent on an empty room and ringing up sales. The clock is usually already running on your lease, and if you're in a mall or a managed property, there's a whole set of landlord and center requirements your construction has to meet exactly. On top of that, your storefront is your first impression — it has to pull people in off the sidewalk.",
      "We handle retail and storefront build-outs from empty shell to grand-opening ready: layout, fixtures, lighting, flooring, finishes, and the storefront itself. You get a realistic schedule and a clear, itemized quote before we start, and a crew that understands mall and landlord build-out rules and works within them so your approvals and inspections don't stall the project.",
      "Throughout the build you'll know exactly where things stand, and if anything unexpected comes up we flag it right away instead of letting it quietly eat your timeline. The result is a space that's built to hold up to real foot traffic and looks the part on day one. Delivered on time and on budget, by a licensed, insured contractor with 20+ years of experience and a former union carpenter on the tools.",
    ],
    beforeAfter: [
      { caption: "A bare retail shell built out into a finished storefront — New Jersey" },
      { caption: "A dated shop refreshed and reopened for foot traffic — NJ mall storefront" },
    ],
    gallery: [
      { alt: "Retail build-out in New Jersey", filename: "retail-construction-contractor-nj-1.jpg" },
      { alt: "Storefront construction in NJ", filename: "retail-construction-contractor-nj-2.jpg" },
      { alt: "Mall storefront build-out in NJ", filename: "retail-construction-contractor-nj-3.jpg" },
      { alt: "Retail store interior build in NJ", filename: "retail-construction-contractor-nj-4.jpg" },
      { alt: "Retail fixtures and finishes in NJ", filename: "retail-construction-contractor-nj-5.jpg" },
      { alt: "Retail construction in New Jersey", filename: "retail-construction-contractor-nj-6.jpg" },
    ],
    faqs: [
      {
        q: "Do you work within mall and landlord build-out requirements?",
        a: "Yes. We're used to working within center and landlord rules, coordinating approvals and inspections so your build stays on track and compliant.",
      },
      {
        q: "Can you deliver in time for my lease or opening date?",
        a: "That's the whole point. We give you a realistic schedule up front and keep the crew moving so you can open on time — and we flag any risk to the date immediately.",
      },
      {
        q: "Do you build the storefront and interior both?",
        a: "Yes — from the storefront and signage support to the full interior: layout, fixtures, lighting, flooring, and finishes.",
      },
      {
        q: "How do you handle pricing and changes?",
        a: "You get a clear, itemized quote before we start. If something unexpected turns up, we explain it and get your approval before acting — no surprise change orders at the end.",
      },
    ],
    siblings: ["restaurant-build-outs", "general-maintenance", "roofing"],
  },
};

export default SERVICES;

export const RESIDENTIAL_SERVICES = Object.values(SERVICES).filter(
  (s) => s.category === "residential"
);
export const COMMERCIAL_SERVICES = Object.values(SERVICES).filter(
  (s) => s.category === "commercial"
);
